import React, { useEffect, useState } from 'react';
import api from '../api/api';

function DriversList() {
    const [drivers, setDrivers] = useState([]);
    const [filteredDrivers, setFilteredDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');

    useEffect(() => {
        fetchDrivers();
    }, []);

    useEffect(() => {
        filterDrivers();
    }, [searchQuery, drivers]);

    const fetchDrivers = async () => {
        setLoading(true);
        try {
            const res = await api.get('/vendor/drivers');
            setDrivers(res.data || []);
        } catch {
            alert('فشل تحميل قائمة السائقين');
        } finally {
            setLoading(false);
        }
    };

    const filterDrivers = () => {
        if (!searchQuery.trim()) {
            setFilteredDrivers(drivers);
        } else {
            const filtered = drivers.filter(driver =>
                driver.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                driver.phone.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredDrivers(filtered);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        filterDrivers();
    };

    const openPasswordModal = (driver) => {
        setSelectedDriver(driver);
        setNewPassword('');
        setCurrentPassword(driver.currentPassword || 'غير متوفر');
        setShowPasswordModal(true);
    };

    const resetDriverPassword = async () => {
        if (!newPassword.trim()) {
            alert('الرجاء إدخال كلمة مرور جديدة');
            return;
        }

        try {
            await api.post(`/vendor/drivers/${selectedDriver.id}/reset-password`, {
                newPassword: newPassword
            });
            alert('تم تحديث كلمة المرور بنجاح');
            setShowPasswordModal(false);
            setNewPassword('');
            setSelectedDriver(null);
        } catch (error) {
            console.error('Error resetting password:', error);
            alert('فشل في تحديث كلمة المرور');
        }
    };

    const openDeleteModal = (driver) => {
        setSelectedDriver(driver);
        setShowDeleteModal(true);
    };

    const deleteDriver = async () => {
        try {
            await api.delete(`/vendor/drivers/${selectedDriver.id}`);
            alert(`تم حذف السائق ${selectedDriver.name} بنجاح`);
            setShowDeleteModal(false);
            setSelectedDriver(null);
            // Refresh the drivers list
            fetchDrivers();
        } catch (error) {
            console.error('Error deleting driver:', error);
            if (error.response?.status === 400) {
                alert(error.response?.data || 'لا يمكن حذف السائق لأنه لديه طلبات نشطة');
            } else if (error.response?.status === 404) {
                alert('السائق غير موجود');
            } else {
                alert('فشل في حذف السائق');
            }
        }
    };

    return (
        <div>
            <h4 className="mb-3">قائمة السائقين المسجلين</h4>
            
            {/* Search Bar */}
            <div className="mb-3">
                <form onSubmit={handleSearch} className="d-flex">
                    <input
                        type="text"
                        className="form-control me-2"
                        placeholder="البحث عن السائقين (الاسم، رقم الهاتف)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button type="submit" className="btn btn-primary me-2" disabled={loading}>
                        بحث
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={() => setSearchQuery('')}
                    >
                        إعادة تعيين
                    </button>
                </form>
            </div>

            {loading ? (
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">جاري التحميل...</span>
                    </div>
                </div>
            ) : (
                <table className="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th>المعرف</th>
                            <th>الاسم</th>
                            <th>رقم الهاتف</th>
                            <th>إدارة كلمة المرور</th>
                            <th>العمليات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDrivers.map(driver => (
                            <tr key={driver.id}>
                                <td>{driver.id}</td>
                                <td>{driver.name}</td>
                                <td>{driver.phone}</td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-warning"
                                        onClick={() => openPasswordModal(driver)}
                                    >
                                        {driver.hasPassword ? 'تغيير كلمة المرور' : 'تعيين كلمة مرور'}
                                    </button>
                                </td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => openDeleteModal(driver)}
                                        title="حذف السائق"
                                    >
                                        حذف
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filteredDrivers.length === 0 && (
                            <tr>
                                <td colSpan="5" className="text-center">
                                    {searchQuery ? 'لم يتم العثور على سائقين' : 'لا يوجد سائقون'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}

            {/* Password Reset Modal */}
            {showPasswordModal && selectedDriver && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {selectedDriver.hasPassword ? 'تغيير كلمة مرور السائق' : 'تعيين كلمة مرور للسائق'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setSelectedDriver(null);
                                        setNewPassword('');
                                        setCurrentPassword('');
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label"><strong>اسم السائق:</strong></label>
                                    <p className="form-control-plaintext">{selectedDriver.name}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label"><strong>رقم الهاتف:</strong></label>
                                    <p className="form-control-plaintext">{selectedDriver.phone}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label"><strong>كلمة المرور الحالية:</strong></label>
                                    <p className="form-control-plaintext" style={{
                                        backgroundColor: '#f8f9fa', 
                                        padding: '8px 12px', 
                                        borderRadius: '4px',
                                        border: '1px solid #dee2e6'
                                    }}>
                                        {currentPassword}
                                    </p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label"><strong>كلمة المرور الجديدة:</strong></label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="أدخل كلمة مرور جديدة"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setSelectedDriver(null);
                                        setNewPassword('');
                                        setCurrentPassword('');
                                    }}
                                >
                                    إغلاق
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={resetDriverPassword}
                                >
                                    {selectedDriver.hasPassword ? 'تحديث كلمة المرور' : 'تعيين كلمة المرور'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedDriver && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">تأكيد حذف السائق</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setSelectedDriver(null);
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-warning" role="alert">
                                    <strong>تحذير:</strong> هذا الإجراء لا يمكن التراجع عنه!
                                </div>
                                <p>هل أنت متأكد من أنك تريد حذف السائق التالي؟</p>
                                <div className="mb-3">
                                    <strong>الاسم:</strong> {selectedDriver.name}
                                </div>
                                <div className="mb-3">
                                    <strong>رقم الهاتف:</strong> {selectedDriver.phone}
                                </div>
                                <div className="alert alert-info" role="alert">
                                    <small>
                                        <strong>ملاحظة:</strong> إذا كان لدى السائق طلبات نشطة، فلن يتم حذفه.
                                        الطلبات المكتملة ستبقى في السجل للمرجعية.
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setSelectedDriver(null);
                                    }}
                                >
                                    إلغاء
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={deleteDriver}
                                >
                                    حذف السائق
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DriversList;
